<template>
  <div>
    <div class="options">
      <div
        ref="optionsList"
        :class="['list', 'gantt_scroll']"
        :style="{maxHeight: `${maxHeight}px`}"
      >
        <Container
          drag-handle-selector=".option-drag-handle"
          :lock-axis="'y'"
          :auto-scroll-enabled="true"
          @drop="onDrop(null, optionItems, $event)"
        >
          <Draggable
            v-for="(item, index) in optionItems"
            :key="index"
            ref="colorOptionItem"
            :data-color-id="item.id"
            :class="['list__item', item.id === targetOptionId ? 'active' : '']"
          >
            <div
              class="colors-option-wrapper"
              @click="clickEdit(item.id, index)"
            >
              <div
                v-if="optionItems.length > 1"
                class="dnd_icon option-drag-handle"
              >
                <svg-sprite
                  :name="'dragdrop'"
                  :size="20"
                />
              </div>
              <div
                :class="['color_bg', item.draggable ? 'option-drag-handle' : '']"
                :style="{background: item.value}"
                @click="togglePalette(item.id, index)"
              />
              <div class="buttons">
                <div
                  class="buttons__arrow"
                  @click="togglePalette(item.id, index)"
                >
                  <svg-sprite
                    :name="'drop-down'"
                    :type="'bold'"
                    :size="24"
                  />
                </div>
                <div
                  v-if="optionItems.length > 1"
                  class="buttons__delete"
                >
                  <icon-button
                    :icon="{ name:'delete', size: 20 }"
                    size="small"
                    @onClick="deleteOption(item.id)"
                  />
                </div>
              </div>
            </div>
          </Draggable>
        </Container>
      </div>
      <div class="button_add">
        <vgp-button
          class="gp_autotest_columns_bar_add_color_option"
          :label="locale('common_add')"
          type="text"
          :icon="{name: 'plus-1', type: 'bold', size: 20}"
          icon-side="left"
          :x-small="true"
          @onClick="addOption"
        />
      </div>
    </div>

    <context-menu
      v-if="isShowColorsDropdown"
      :position="positionColorsDropdown"
      @onClickOutside="onClickOutside"
    >
      <template #body>
        <vgp-colors-list
          :colors="colors"
          :selected="[selectedColorId]"
          @selectColors="onChangeColor"
        />
      </template>
    </context-menu>
  </div>
</template>

<script>
import { Container, Draggable } from 'vue-smooth-dnd';
import iconButton from '../../../../common/VueComponents/globalButton/withIcon/iconButton.vue';
import vgpButton from '../../../../common/VueComponents/globalButton/vgpButton.vue';
import vgpColorsList from '../../../../common/VueComponents/colorPalette/colorsList/colorsList.vue';
import columnsBarHelpers from '../../helpers';
import svgSprite from '../../../../common/VueComponents/VueIcon/svgSprite.vue';

export default {
  name: 'ColumnsBarColorOptions',
  components: {
    vgpColorsList, vgpButton, iconButton, Container, Draggable, svgSprite,
  },
  props: {
    options: { type: Array, requied: false, default: () => [] },
    maxHeight: { type: Number, required: false, default: 500 },
  },
  emits: {
    onChange: Array,
  },
  data() {
    return {
      locale: __,
      optionItems: [],
      usedColors: [],
      targetOptionId: null,
      selectedColorId: null,
      positionColorsDropdown: null,
      isShowColorsDropdown: false,
    };
  },
  computed: {
    colors() {
      return this.$store.getters['teamSetting/colors'].data;
    },
  },
  watch: {
    options: {
      handler(options) {
        if (options && options.length) {
          this.setOptions(options);
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.optionItems = [];
    this.usedColors = [];
  },
  created() {
    if (this.options && this.options.length) {
      this.setOptions(this.options);
    } else {
      this.setDefault();
    }

    this.$emit('onChange', this.optionItems);
  },
  methods: {
    setDefault() {
      const defaultColor = this.colors.find(color => color.id === 2);

      const item = {
        id: new Date().getTime(),
        colorId: defaultColor.id,
        value: defaultColor.hex,
        is_default: 0,
        sort_order: this.optionItems.length,
        draggable: true,
      };

      this.optionItems.push(item);
      this.usedColors.push(defaultColor);
    },
    setOptions(options) {
      let newOptionsItems = [];
      let newUsedColors = [];

      if (this.optionItems.length) {
        const existingOptions = this.optionItems.filter(opt => options.find(item => item.id === opt.id || opt.newLocalOption));

        existingOptions.forEach(opt => {
          const externalOption = options.find(item => item.id === opt.id);
          const color = this.colors.find(color => color.hex === externalOption.value);

          if (opt.changedLocalOption || opt.newLocalOption) {
            newOptionsItems.push(opt);
          } else {
            newOptionsItems.push({
              id: externalOption.id,
              colorId: color.id,
              value: externalOption.value,
              is_default: externalOption.is_default,
              sort_order: externalOption.sort_order,
              readonly: true,
            });
          }

          newUsedColors.push(color);
        });
      } else {
        options.forEach((opt, index) => {
          const color = this.colors.find(color => color.hex === opt.value);

          newOptionsItems.push({
            id: opt.id,
            colorId: color.id,
            value: opt.value,
            is_default: opt.is_default,
            sort_order: index,
            readonly: true,
          });

          newUsedColors.push(color);
        });
      }

      newOptionsItems.sort((a, b) => a.sort_order - b.sort_order);

      this.optionItems = newOptionsItems;
      this.usedColors = newUsedColors;
    },
    togglePalette(id, index) {
      this.isShowColorsDropdown = !this.isShowColorsDropdown;

      const colorId = this.optionItems.find(opt => opt.id === id).colorId;
      const optionElement = this.$refs.colorOptionItem[index].$el;

      if (this.isShowColorsDropdown) {
        this.targetOptionId = id;
        this.positionColorsDropdown = optionElement.getBoundingClientRect();
        this.selectedColorId = +colorId;
      } else {
        this.targetOptionId = null;
      }
    },
    deleteOption(id) {
      this.optionItems = this.optionItems.filter(opt => opt.id !== id);
      this.$emit('onChange', this.optionItems);
    },
    addOption() {
      const randomColor = columnsBarHelpers.getRandomColor(this.colors, this.usedColors);

      const item = {
        id: new Date().getTime(),
        colorId: randomColor.id,
        value: randomColor.hex,
        is_default: 0,
        sort_order: this.optionItems.length,
        draggable: true,
        newLocalOption: true,
      };

      this.optionItems.push(item);
      this.usedColors.push(randomColor);
      this.$emit('onChange', this.optionItems);

      setTimeout(() => {
        this.scrollList();
      }, 0);
    },
    onChangeColor(data) {
      const newColor = data[0];
      const currentOption = this.optionItems.find(opt => opt.id === this.targetOptionId);

      if (!currentOption) return;

      const index = this.optionItems.indexOf(currentOption);

      this.optionItems.splice(index, 1, {
        ...currentOption,
        colorId: newColor.id,
        value: newColor.hex,
        draggable: true,
        changedLocalOption: true,
      });
      this.$emit('onChange', this.optionItems);
      this.onClickOutside();
    },
    onClickOutside() {
      this.isShowColorsDropdown = false;
      this.targetOptionId = null;

      this.optionItems.forEach(opt => {
        opt.draggable = true;
      });
    },
    scrollList() {
      const list = this.$refs.optionsList;

      list.scrollTop = list.scrollHeight;
    },
    clickEdit(id, index) {
      this.optionItems.forEach(opt => {
        if (opt.id === id) {
          opt.draggable = !opt.draggable;
        }
      });
    },
    onDrop(id, optionItems, dropResult) {
      if (dropResult.removedIndex === null && dropResult.addedIndex === null) return optionItems;
      if (dropResult.removedIndex === dropResult.addedIndex) return optionItems;

      const result = [...optionItems];
      let itemToAdd = null;

      if (dropResult.removedIndex !== null) {
        itemToAdd = result.splice(dropResult.removedIndex, 1)[0];
      }

      if (dropResult.addedIndex !== null) {
        result.splice(dropResult.addedIndex, 0, itemToAdd);
      }

      this.optionItems = [];

      result.forEach((option, idx) => {
        this.optionItems.push({ ...option, sort_order: idx });
      });

      this.$emit('onChange', this.optionItems);
    },
  },
};
</script>

<style src='./style.less' lang='less'></style>
